import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useBIMStore } from './useBIMStore';
import { clashDetectionManager, syncClashRegistry } from '../core/services';
import type { ClashReport } from '../core/ClashDetectionManager';
import { notify } from '../utils/notify';
import i18n from '../i18n';

/**
 * Worksharing store backing the Collaborate tab.
 *
 * Mirrors BIM's central/local model workflow in a local simulation:
 * the "central model" is a snapshot persisted under the
 * 'yap-central-model' localStorage key (the same key the
 * executeSynchronize command uses), so Sync/Reload round-trip real
 * project data between local and central copies.
 */

export type CentralLocation = 'none' | 'network' | 'cloud';

export interface Workset {
    id: string;
    name: string;
    /** Shown when the model opens (BIM "Visible by default"). */
    visibleByDefault: boolean;
    /** System worksets (Project Information, Shared Levels and Grids) cannot be deleted. */
    isSystem: boolean;
    /** User who opened the workset for editing; null = nobody owns it. */
    owner: string | null;
}

export interface MonitoredSnapshotEntry {
    id: string;
    name: string;
    /** Levels: elevation; Grids: start; Columns: position. */
    value: number[];
}

export interface MonitoredModel {
    id: string;
    /** Source display name (link model or "Current Project"). */
    sourceName: string;
    monitorLevels: boolean;
    monitorGrids: boolean;
    monitorColumns: boolean;
    /** Datum geometry captured when monitoring started (drives Coordination Review). */
    snapshotLevels: MonitoredSnapshotEntry[];
    snapshotGrids: MonitoredSnapshotEntry[];
    snapshotColumns: MonitoredSnapshotEntry[];
}

export type CoordinationIssueKind = 'Level' | 'Grid' | 'Column';
export type CoordinationIssueStatus = 'New' | 'Postponed' | 'Resolved' | 'Rejected';

export interface CoordinationIssue {
    id: string;
    monitorId: string;
    monitorName: string;
    kind: CoordinationIssueKind;
    datumId: string;
    name: string;
    description: string;
    status: CoordinationIssueStatus;
    detectedAt: number;
}

export interface CloudModelEntry {
    id: string;
    name: string;
    hub: string;
    version: number;
    lastSyncedAt: number | null;
}

export interface PublishTarget {
    id: string;
    name: string;
    kind: 'sheet' | 'view';
    published: boolean;
}

export interface InterferenceRow {
    id: string;
    type: string;
    categoryA: string;
    typeA: string;
    categoryB: string;
    typeB: string;
    distance: number;
    point: [number, number, number];
}

export interface InterferenceReport {
    runAt: number;
    modelA: string;
    modelB: string;
    tolerance: number;
    total: number;
    rows: InterferenceRow[];
}

interface WorksharingState {
    // ---- Model / central state ----
    worksharingEnabled: boolean;
    centralLocation: CentralLocation;
    centralPath: string;
    modelName: string;
    userName: string;
    syncCount: number;
    lastSyncAt: number | null;
    /** Saved options reused by "Synchronize Now" (BIM remembers last settings). */
    syncSettings: { relinquishAfterSync: boolean; compactCentral: boolean };
    /** Element id -> workset id (BIM element/workset ownership). */
    assignments: Record<string, string>;

    // ---- Worksets ----
    worksets: Workset[];
    activeWorksetId: string;

    // ---- Cloud / Communicate ----
    cloudModels: CloudModelEntry[];
    publishTargets: PublishTarget[];

    // ---- Coordinate ----
    monitoredModels: MonitoredModel[];
    coordinationIssues: CoordinationIssue[];
    lastInterferenceReport: InterferenceReport | null;

    // ---- Actions ----
    setUserName: (name: string) => void;
    startWorksharing: (location: CentralLocation, path: string, modelName: string) => void;
    /** Assign every project element without a workset to the active workset. */
    syncAssignmentsWithProject: () => void;
    addWorkset: (name: string, visibleByDefault: boolean) => string | null;
    renameWorkset: (id: string, name: string) => void;
    deleteWorkset: (id: string) => boolean;
    setWorksetVisibleByDefault: (id: string, visible: boolean) => void;
    setActiveWorkset: (id: string) => void;
    /** Persist the "Synchronize with Central – Modify Settings" options. */
    updateSyncSettings: (settings: Partial<WorksharingState['syncSettings']>) => void;
    /** Open/close a workset for editing by the current user (ownership). */
    toggleWorksetEditable: (id: string) => void;
    /** Assign the given elements to a workset (BIM: selection > workset). */
    assignElements: (ids: string[], worksetId: string) => void;
    relinquishAllMine: () => number;
    synchronizeWithCentral: (comment: string, relinquishAfter: boolean) => Promise<boolean>;
    reloadLatest: () => Promise<boolean>;

    setPublishTargets: () => void;
    togglePublishTarget: (id: string) => void;
    deleteCloudModel: (id: string) => void;

    addMonitoredModel: (sourceName: string, opts: { levels: boolean; grids: boolean; columns: boolean }) => void;
    removeMonitoredModel: (id: string) => void;
    /** Diff every monitor snapshot against live datum geometry; returns new issue count. */
    refreshCoordinationIssues: () => number;
    setIssueStatus: (id: string, status: CoordinationIssueStatus) => void;
    /** Accept/Reject/Postpone visible issues in bulk. */
    setIssueStatusBulk: (ids: string[], status: CoordinationIssueStatus) => void;

    runInterferenceCheck: (modelA: string, modelB: string, tolerance: number) => InterferenceReport;
}

const CENTRAL_STORAGE_KEY = 'yap-central-model';

let idCounter = 0;
function uid(prefix: string): string {
    idCounter += 1;
    return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

const systemWorksets = (): Workset[] => [
    { id: 'ws-project-info', name: 'Project Information', visibleByDefault: true, isSystem: true, owner: null },
    { id: 'ws-levels-grids', name: 'Shared Levels and Grids', visibleByDefault: true, isSystem: true, owner: null },
];

/** Read live datum geometry from the BIM store for snapshot/diff operations. */
function readCurrentDatums() {
    const project = useBIMStore.getState().project;
    const levels: MonitoredSnapshotEntry[] = [];
    project.levels.forEach((l) => {
        levels.push({ id: l.id, name: l.name, value: [l.elevation] });
    });
    const grids: MonitoredSnapshotEntry[] = [];
    project.grids.forEach((g) => {
        grids.push({ id: g.id, name: g.name, value: [g.start[0], g.start[1], g.end[0], g.end[1]] });
    });
    const columns: MonitoredSnapshotEntry[] = [];
    project.elements.forEach((el) => {
        if (el.category !== 'Structural Columns') return;
        const pos = (el as unknown as { geometryData?: { location?: [number, number, number] } }).geometryData?.location;
        columns.push({ id: el.id, name: el.name || el.type, value: pos ? [pos[0], pos[1], pos[2]] : [0, 0, 0] });
    });
    return { levels, grids, columns };
}

function sameValue(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((v, i) => Math.abs(v - b[i]) < 1e-4);
}

function describeChange(kind: CoordinationIssueKind, before: number[], after: number[]): string {
    if (kind === 'Level') {
        return `Elevation changed from ${before[0].toFixed(2)} m to ${after[0].toFixed(2)} m.`;
    }
    if (kind === 'Grid') {
        return `Grid line moved from (${before[0].toFixed(2)}, ${before[1].toFixed(2)}) to (${after[0].toFixed(2)}, ${after[1].toFixed(2)}).`;
    }
    return `Column moved from (${before[0].toFixed(2)}, ${before[1].toFixed(2)}) to (${after[0].toFixed(2)}, ${after[1].toFixed(2)}).`;
}

export const useWorksharingStore = create<WorksharingState>()(
    persist(
        immer((set, get) => ({
            worksharingEnabled: false,
            centralLocation: 'none',
            centralPath: '',
            modelName: '',
            userName: 'Local Architect',
            syncCount: 0,
            lastSyncAt: null,
            syncSettings: { relinquishAfterSync: false, compactCentral: false },
            assignments: {},

            worksets: systemWorksets(),
            activeWorksetId: 'ws-levels-grids',

            cloudModels: [],
            publishTargets: [],

            monitoredModels: [],
            coordinationIssues: [],
            lastInterferenceReport: null,

            setUserName: (name) => set((state) => { state.userName = name; }),

            startWorksharing: (location, path, modelName) => {
                const s = get();
                if (s.worksharingEnabled) {
                    notify(i18n.t('tools.notify.worksharingIsAlreadyEnabledForThis'), 'warning');
                    return;
                }
                set((state) => {
                    state.worksharingEnabled = true;
                    state.centralLocation = location;
                    state.centralPath = path;
                    state.modelName = modelName;
                });
                get().syncAssignmentsWithProject();
                if (location === 'cloud') {
                    set((state) => {
                        state.cloudModels = [
                            { id: uid('cloud'), name: modelName, hub: path || 'ACME Hub / BIM 360', version: 1, lastSyncedAt: Date.now() },
                            ...state.cloudModels,
                        ];
                    });
                }
                notify(i18n.t('tools.notify.collabEnabled', { where: location === 'cloud' ? i18n.t('tools.notify.cloudLocation') : i18n.t('tools.notify.networkLocation') }), 'success');
            },

            syncAssignmentsWithProject: () => {
                const project = useBIMStore.getState().project;
                const active = get().activeWorksetId;
                set((state) => {
                    project.elements.forEach((el) => {
                        if (!state.assignments[el.id]) {
                            state.assignments[el.id] = active;
                        }
                    });
                });
            },

            addWorkset: (name, visibleByDefault) => {
                const trimmed = name.trim();
                if (!trimmed) return null;
                if (get().worksets.some((w) => w.name.toLowerCase() === trimmed.toLowerCase())) {
                    notify(i18n.t('tools.notify.worksetExists', { name: trimmed }), 'error');
                    return null;
                }
                const id = uid('ws');
                set((state) => {
                    state.worksets.push({ id, name: trimmed, visibleByDefault, isSystem: false, owner: null });
                });
                return id;
            },

            renameWorkset: (id, name) => {
                const trimmed = name.trim();
                if (!trimmed) return;
                set((state) => {
                    const ws = state.worksets.find((w) => w.id === id);
                    if (ws) ws.name = trimmed;
                });
            },

            deleteWorkset: (id) => {
                const ws = get().worksets.find((w) => w.id === id);
                if (!ws) return false;
                if (ws.isSystem) {
                    notify(i18n.t('tools.notify.systemWorksetsCannotBeDeleted'), 'warning');
                    return false;
                }
                const assigned = Object.values(get().assignments).filter((w) => w === id).length;
                if (assigned > 0) {
                    notify(i18n.t('tools.notify.worksetDeleteBlocked', { name: ws.name, count: assigned }), 'error');
                    return false;
                }
                set((state) => {
                    state.worksets = state.worksets.filter((w) => w.id !== id);
                    if (state.activeWorksetId === id) {
                        state.activeWorksetId = state.worksets[0]?.id ?? '';
                    }
                });
                return true;
            },

            setWorksetVisibleByDefault: (id, visible) => {
                set((state) => {
                    const ws = state.worksets.find((w) => w.id === id);
                    if (ws) ws.visibleByDefault = visible;
                });
            },

            setActiveWorkset: (id) => set((state) => { state.activeWorksetId = id; }),

            updateSyncSettings: (settings) => set((state) => {
                if (settings.relinquishAfterSync !== undefined) state.syncSettings.relinquishAfterSync = settings.relinquishAfterSync;
                if (settings.compactCentral !== undefined) state.syncSettings.compactCentral = settings.compactCentral;
            }),

            toggleWorksetEditable: (id) => {
                const me = get().userName;
                set((state) => {
                    const ws = state.worksets.find((w) => w.id === id);
                    if (!ws) return;
                    if (ws.owner === null) {
                        ws.owner = me;
                    } else if (ws.owner === me) {
                        ws.owner = null;
                    } else {
                        notify(i18n.t('tools.notify.worksetOwned', { name: ws.name, owner: ws.owner }), 'warning');
                    }
                });
            },

            assignElements: (ids, worksetId) => {
                set((state) => {
                    for (const id of ids) {
                        state.assignments[id] = worksetId;
                    }
                });
            },

            relinquishAllMine: () => {
                const me = get().userName;
                let count = 0;
                set((state) => {
                    for (const ws of state.worksets) {
                        if (ws.owner === me) {
                            ws.owner = null;
                            count += 1;
                        }
                    }
                });
                notify(
                    count > 0
                        ? `Relinquished ${count} workset(s) owned by ${me}.`
                        : 'Nothing owned by you to relinquish.',
                    count > 0 ? 'success' : 'info'
                );
                return count;
            },

            synchronizeWithCentral: async (comment, relinquishAfter) => {
                if (!get().worksharingEnabled) {
                    notify(i18n.t('tools.notify.thisModelIsNotYetSet'), 'warning');
                    return false;
                }
                const bimStore = useBIMStore.getState();
                try {
                    const json = await bimStore.saveProject();
                    window.localStorage.setItem(CENTRAL_STORAGE_KEY, json);
                    get().syncAssignmentsWithProject();
                    set((state) => {
                        state.syncCount += 1;
                        state.lastSyncAt = Date.now();
                        state.cloudModels = state.cloudModels.map((cm) =>
                            cm.name === state.modelName
                                ? { ...cm, version: cm.version + 1, lastSyncedAt: Date.now() }
                                : cm
                        );
                    });
                    if (relinquishAfter) get().relinquishAllMine();
                    const suffix = comment.trim() ? ` Comment: "${comment.trim()}".` : '';
                    notify(i18n.t('tools.notify.synchronized', { suffix: suffix }), 'success');
                    return true;
                } catch (err) {
                    console.error('[Worksharing] Synchronize failed:', err);
                    notify(i18n.t('tools.notify.synchronizationFailedCheckTheConsoleFor'), 'error');
                    return false;
                }
            },

            reloadLatest: async () => {
                if (!get().worksharingEnabled) {
                    notify(i18n.t('tools.notify.thisModelIsNotSetUp'), 'warning');
                    return false;
                }
                const json = window.localStorage.getItem(CENTRAL_STORAGE_KEY);
                if (!json) {
                    notify(i18n.t('tools.notify.noCentralModelFoundYetSynchronize'), 'warning');
                    return false;
                }
                try {
                    await useBIMStore.getState().loadProject(json);
                    notify(i18n.t('tools.notify.reloadedLatestFromCentralModel'), 'success');
                    return true;
                } catch (err) {
                    console.error('[Worksharing] Reload failed:', err);
                    notify(i18n.t('tools.notify.reloadLatestFailedTheCentralSnapshot'), 'error');
                    return false;
                }
            },

            setPublishTargets: () => {
                const project = useBIMStore.getState().project;
                const targets: PublishTarget[] = [];
                const existing = new Map(get().publishTargets.map((t) => [t.id, t.published]));
                project.sheets.forEach((sheet) => {
                    targets.push({
                        id: sheet.id,
                        name: `${sheet.number} — ${sheet.name}`,
                        kind: 'sheet',
                        published: existing.get(sheet.id) ?? true,
                    });
                });
                project.views.forEach((view) => {
                    if (view.type === '3D') {
                        targets.push({
                            id: view.id,
                            name: view.name,
                            kind: 'view',
                            published: existing.get(view.id) ?? true,
                        });
                    }
                });
                set({ publishTargets: targets });
            },

            togglePublishTarget: (id) => {
                set((state) => {
                    const t = state.publishTargets.find((x) => x.id === id);
                    if (t) t.published = !t.published;
                });
            },

            deleteCloudModel: (id) => {
                set((state) => {
                    state.cloudModels = state.cloudModels.filter((cm) => cm.id !== id);
                });
                notify(i18n.t('tools.notify.cloudModelRemovedFromTheHub'), 'info');
            },

            addMonitoredModel: (sourceName, opts) => {
                const datums = readCurrentDatums();
                set((state) => {
                    state.monitoredModels.push({
                        id: uid('mon'),
                        sourceName,
                        monitorLevels: opts.levels,
                        monitorGrids: opts.grids,
                        monitorColumns: opts.columns,
                        snapshotLevels: datums.levels,
                        snapshotGrids: datums.grids,
                        snapshotColumns: datums.columns,
                    });
                });
                notify(i18n.t('tools.notify.monitoringStarted', { name: sourceName }), 'success');
            },

            removeMonitoredModel: (id) => {
                set((state) => {
                    state.monitoredModels = state.monitoredModels.filter((m) => m.id !== id);
                    state.coordinationIssues = state.coordinationIssues.filter((i) => i.monitorId !== id);
                });
            },

            refreshCoordinationIssues: () => {
                const { monitoredModels, coordinationIssues } = get();
                if (monitoredModels.length === 0) {
                    notify(i18n.t('tools.notify.noMonitoredModelsUseCopyMonitor'), 'warning');
                    return 0;
                }
                const datums = readCurrentDatums();
                const fresh: CoordinationIssue[] = [];
                for (const mon of monitoredModels) {
                    const diff = (
                        kind: CoordinationIssueKind,
                        snapshot: MonitoredSnapshotEntry[],
                        current: MonitoredSnapshotEntry[]
                    ) => {
                        for (const snap of snapshot) {
                            const now = current.find((c) => c.id === snap.id);
                            if (!now) {
                                fresh.push({
                                    id: uid('iss'),
                                    monitorId: mon.id,
                                    monitorName: mon.sourceName,
                                    kind,
                                    datumId: snap.id,
                                    name: snap.name,
                                    description: `${kind} "${snap.name}" was deleted from ${mon.sourceName}.`,
                                    status: 'New',
                                    detectedAt: Date.now(),
                                });
                            } else if (!sameValue(snap.value, now.value)) {
                                fresh.push({
                                    id: uid('iss'),
                                    monitorId: mon.id,
                                    monitorName: mon.sourceName,
                                    kind,
                                    datumId: snap.id,
                                    name: now.name,
                                    description: describeChange(kind, snap.value, now.value),
                                    status: 'New',
                                    detectedAt: Date.now(),
                                });
                            }
                        }
                    };
                    if (mon.monitorLevels) diff('Level', mon.snapshotLevels, datums.levels);
                    if (mon.monitorGrids) diff('Grid', mon.snapshotGrids, datums.grids);
                    if (mon.monitorColumns) diff('Column', mon.snapshotColumns, datums.columns);
                }
                // Merge: keep decisions on known items, add new detections.
                const known = new Set(coordinationIssues.map((i) => `${i.monitorId}|${i.kind}|${i.datumId}`));
                const merged = [
                    ...coordinationIssues,
                    ...fresh.filter((f) => !known.has(`${f.monitorId}|${f.kind}|${f.datumId}`)),
                ];
                set({ coordinationIssues: merged });
                const added = merged.length - coordinationIssues.length;
                if (added > 0) {
                    notify(i18n.t('tools.notify.coordReviewChanges', { count: added }), 'warning');
                } else {
                    notify(i18n.t('tools.notify.noNewCoordinationChangesFound'), 'info');
                }
                return added;
            },

            setIssueStatus: (id, status) => {
                set((state) => {
                    const issue = state.coordinationIssues.find((i) => i.id === id);
                    if (issue) issue.status = status;
                });
            },

            setIssueStatusBulk: (ids, status) => {
                const idSet = new Set(ids);
                set((state) => {
                    for (const issue of state.coordinationIssues) {
                        if (idSet.has(issue.id)) issue.status = status;
                    }
                });
            },

            runInterferenceCheck: (modelA, modelB, tolerance) => {
                syncClashRegistry();
                const project = useBIMStore.getState().project;
                const linkedCategories = ['BIM Links', 'CAD Links', 'IFC Links', 'Point Cloud', 'Other Links'];
                const idsFor = (name: string): string[] => {
                    const ids: string[] = [];
                    if (name === 'Current Project') {
                        project.elements.forEach((el) => {
                            if (!linkedCategories.includes(el.category)) ids.push(el.id);
                        });
                    } else {
                        project.elements.forEach((el) => {
                            const file = el.parameters?.['File Path'];
                            const short = typeof file === 'string' && file ? (file.split(/[\\/]/).pop() || file) : '';
                            if (linkedCategories.includes(el.category) && short === name) ids.push(el.id);
                        });
                    }
                    return ids;
                };
                const test = clashDetectionManager.createClashTest(
                    `Interference ${modelA} vs ${modelB}`,
                    { elementIds: idsFor(modelA) },
                    { elementIds: idsFor(modelB) },
                    tolerance
                );
                const report: ClashReport = clashDetectionManager.runClashTest(test.id);
                const rows: InterferenceRow[] = report.clashes.map((c) => ({
                    id: c.id,
                    type: c.type,
                    categoryA: c.elementA.category,
                    typeA: c.elementA.type,
                    categoryB: c.elementB.category,
                    typeB: c.elementB.type,
                    distance: c.distance,
                    point: [c.point.x, c.point.y, c.point.z],
                }));
                const result: InterferenceReport = {
                    runAt: Date.now(),
                    modelA,
                    modelB,
                    tolerance,
                    total: rows.length,
                    rows,
                };
                set({ lastInterferenceReport: result });
                notify(
                    rows.length > 0
                        ? `Interference check finished: ${rows.length} clash(es) found.`
                        : 'Interference check finished: no interferences found.',
                    rows.length > 0 ? 'warning' : 'success'
                );
                return result;
            },
        })),
        {
            name: 'yap-worksharing',
            partialize: (state) => ({
                worksharingEnabled: state.worksharingEnabled,
                centralLocation: state.centralLocation,
                centralPath: state.centralPath,
                modelName: state.modelName,
                userName: state.userName,
                syncCount: state.syncCount,
                lastSyncAt: state.lastSyncAt,
                syncSettings: state.syncSettings,
                assignments: state.assignments,
                worksets: state.worksets,
                activeWorksetId: state.activeWorksetId,
                cloudModels: state.cloudModels,
                publishTargets: state.publishTargets,
                monitoredModels: state.monitoredModels,
                coordinationIssues: state.coordinationIssues,
            }),
        }
    )
);

/** Distinct model names available for interference checking. */
export function getInterferenceModelOptions(): string[] {
    const project = useBIMStore.getState().project;
    const names = new Set<string>(['Current Project']);
    project.elements.forEach((el) => {
        const file = el.parameters?.['File Path'];
        if (typeof file === 'string' && file) {
            const short = file.split(/[\\/]/).pop() || file;
            names.add(short);
        }
    });
    return Array.from(names);
}
